// =============================================================================
// ui/state/backtests.tsx — the back-test sample store.
// -----------------------------------------------------------------------------
// Loaded once at the shell, like patterns. Unlike patterns it also WRITES: the
// Backtesting tab edits samples inline, so this holds the list and the three
// mutators (add / update / remove) that persist each change and update the list
// optimistically — the tab renders straight off `records`.
//
// ── ⚠ OPTIMISTIC, AND A FAILED WRITE IS RETURNED, NOT SWALLOWED ──
//
// beta pushes the record and fires a whole-state save; the equivalent here writes
// one row. The local list updates immediately so an inline edit feels instant,
// and the io error (if any) is RETURNED to the caller so the row can say "not
// saved" — the alternative, reverting the field the user just typed, is worse.
// A failed DELETE reloads, because a phantom re-appearing is clearer than a row
// that looks gone but is not.
//
// ⚠ NULL until a read succeeds — an empty book and a failed read are different
// states, the same reason `loadPatterns` throws rather than returning [].
// =============================================================================

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  deleteBacktest, insertBacktest, loadBacktests, saveBacktest, type BacktestRecord,
} from '../../io/sync/backtests';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface BacktestsState {
  phase: 'loading' | 'ready' | 'failed';
  records: readonly BacktestRecord[] | null;
  error: string | null;
  /** Insert a brand-new sample. */
  add: (rec: BacktestRecord) => Promise<{ error: string | null }>;
  /** Persist an inline edit to an existing sample. */
  update: (rec: BacktestRecord) => Promise<{ error: string | null }>;
  /** Delete a sample. */
  remove: (id: string) => Promise<{ error: string | null }>;
  reload: () => void;
}

const Ctx = createContext<BacktestsState | null>(null);

export function useBacktests(): BacktestsState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useBacktests() outside <BacktestsProvider>');
  return v;
}

export function BacktestsProvider(
  { sb, userId, children }: { sb: SupabaseClient; userId: string; children: ReactNode },
) {
  const [phase, setPhase] = useState<BacktestsState['phase']>('loading');
  const [records, setRecords] = useState<readonly BacktestRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setError(null);
    void (async () => {
      try {
        const recs = await loadBacktests(sb);
        if (cancelled) return;
        setRecords(recs);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        setError(reportFailure('backtests', e));
        setPhase('failed');
      }
    })();
    return () => { cancelled = true; };
  }, [sb, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const add = useCallback(async (rec: BacktestRecord) => {
    setRecords((cur) => [...(cur ?? []), rec]);
    const res = await insertBacktest(sb, rec, userId);
    return { error: res.error };
  }, [sb, userId]);

  const update = useCallback(async (rec: BacktestRecord) => {
    setRecords((cur) => (cur ?? []).map((b) => (b.id === rec.id ? rec : b)));
    const res = await saveBacktest(sb, rec, userId);
    return { error: res.error };
  }, [sb, userId]);

  const remove = useCallback(async (id: string) => {
    setRecords((cur) => (cur ?? []).filter((b) => b.id !== id));
    const res = await deleteBacktest(sb, userId, id);
    // ⚠ A failed delete: pull the truth back rather than leave a row that looks gone.
    if (res.error) setNonce((n) => n + 1);
    return { error: res.error };
  }, [sb, userId]);

  const value = useMemo<BacktestsState>(
    () => ({ phase, records, error, add, update, remove, reload }),
    [phase, records, error, add, update, remove, reload],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
